<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col p-4 pb-24">
    <div class="mb-4 text-sm opacity-70">
      {{ t('views.more.quickLinks.description') }}
    </div>

    <div class="space-y-3">
      <div
        v-for="action in AVAILABLE_ACTIONS"
        :key="action.id"
        @click="toggleAction(action.id)"
        class="flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer"
        :class="isSelected(action.id) 
          ? 'bg-[var(--color-primary-200)] border-[var(--color-primary-500)] shadow-md' 
          : 'bg-[var(--color-primary-100)] border-[var(--color-primary-300)] opacity-80'"
      >
        <div class="flex items-center gap-3">
          <div class="p-2 rounded-full" :class="isSelected(action.id) ? 'bg-[var(--color-primary-300)]' : 'bg-[var(--color-primary-200)]'">
            <component :is="action.icon" class="w-5 h-5" />
          </div>
          <span class="font-medium">{{ t(action.labelKey) }}</span>
        </div>
        
        <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center"
             :class="isSelected(action.id) ? 'border-[var(--color-success)] bg-[var(--color-success)]' : 'border-[var(--color-primary-400)]'">
          <Check v-if="isSelected(action.id)" class="w-4 h-4 text-white" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUiStore } from '@/stores/ui.store.js'
import { useQuickActions } from '@/utils/useQuickActions.js'
import { Check } from 'lucide-vue-next'

const { t } = useI18n()
const uiStore = useUiStore()
const { AVAILABLE_ACTIONS, selectedActionIds } = useQuickActions()

function isSelected(id) {
  return selectedActionIds.value.includes(id)
}

function toggleAction(id) {
  if (isSelected(id)) {
    // Prevent removing the last one if you want to enforce at least one, otherwise allow empty
    selectedActionIds.value = selectedActionIds.value.filter(item => item !== id)
  } else {
    if (selectedActionIds.value.length >= 3) {
      return
    }
    selectedActionIds.value.push(id)
  }
}

onMounted(() => {
  uiStore.setHeader({ title: t('views.more.quickLinks.title'), showBack: true })
})
</script>